import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Download,
  FileText,
  FlaskConical
} from 'lucide-react';
import { apiClient } from '../lib/api';
import { LabResultDetail as LabResultDetailData } from '../types/labResults';
import {
  LabAbnormalSummary,
  LabResultTable,
  LabStatusBadge
} from '../components/labs/LabResultTable';
import { formatResultDate } from '../components/labs/PatientLabResults';

/**
 * A single lab report, for the clinic and for Bloom admins.
 *
 * The clinician counterpart of DtpResultDetail. Both render the observations
 * through the same LabResultTable; what differs here is the surrounding
 * metadata — accession, ordering provider, amendment history — which is
 * operational rather than clinical and belongs to the people who have to ring
 * the lab about it.
 */
export const LabResultDetail: React.FC = () => {
  const { resultId } = useParams<{ resultId: string }>();
  const navigate = useNavigate();

  const [result, setResult] = useState<LabResultDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!resultId) return;
    setLoading(true);
    apiClient
      .getLabResult(resultId)
      .then(res => {
        if (res.success && res.data) {
          setResult(res.data);
          setError('');
        } else {
          setError(res.error || 'Lab result not found');
        }
      })
      .finally(() => setLoading(false));
  }, [resultId]);

  const openAttachment = async (index: number) => {
    if (!resultId) return;
    const res = await apiClient.getLabResultAttachmentUrl(resultId, index);
    if (res.success && res.data?.url) {
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } else {
      setError(res.error || 'Could not open the report');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Activity className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error && !result) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-red-500" />
        <p className="mt-4 text-gray-700">{error}</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-6 text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          Go back
        </button>
      </div>
    );
  }

  if (!result) return null;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
      <button
        onClick={() => navigate(result.patientId ? `/patients/${result.patientId}` : -1 as any)}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to patient
      </button>

      <div className="rounded-lg bg-white shadow">
        <div className="border-b border-gray-200 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <FlaskConical className="h-5 w-5 text-gray-400" />
                <h1 className="text-xl font-semibold text-gray-900">{result.panelName}</h1>
                <LabStatusBadge status={result.status} amended={result.amended} />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Accession {result.vendorAccession}
                {result.version > 1 ? ` · version ${result.version}` : ''}
                {' · '}
                {formatResultDate(result)}
              </p>
            </div>
            <LabAbnormalSummary
              total={result.observationCount}
              abnormal={result.abnormalCount}
            />
          </div>

          {result.status === 'preliminary' && (
            <div className="mt-4 rounded-md bg-yellow-50 p-3 text-sm text-yellow-800">
              These values are preliminary and may change when the lab finalizes the
              report. The patient has not been shown them or notified.
            </div>
          )}

          {result.amended && (
            <div className="mt-4 rounded-md bg-purple-50 p-3 text-sm text-purple-800">
              A newer version of this report exists. The values below are the ones
              from version {result.version} and have been superseded.
            </div>
          )}

          {result.orderingProvider && (
            <p className="mt-4 text-sm text-gray-600">
              Ordered by {result.orderingProvider.firstName} {result.orderingProvider.lastName}
              {result.orderingProvider.npi ? ` · NPI ${result.orderingProvider.npi}` : ''}
            </p>
          )}

          {result.attachments.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {result.attachments.map(a => (
                <button
                  key={a.index}
                  disabled={!a.available}
                  onClick={() => openAttachment(a.index)}
                  className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm ${
                    a.available
                      ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      : 'cursor-not-allowed border-gray-200 text-gray-400'
                  }`}
                  title={a.available ? undefined : 'This report could not be stored when it arrived'}
                >
                  {a.available ? <Download className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                  {a.fileName || 'Lab report (PDF)'}
                  {!a.available && ' — unavailable'}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-4 py-2 sm:px-6">
          <LabResultTable observations={result.observations} reportNotes={result.reportNotes} />
        </div>

        {result.versions.length > 1 && (
          <div className="border-t border-gray-200 p-6">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Report history
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              Amendments are stored as separate reports rather than overwriting the
              original, so every version the lab sent stays readable.
            </p>
            <div className="mt-3 space-y-1">
              {result.versions.map(v => (
                <button
                  key={v.id}
                  onClick={() => navigate(`/lab-results/${v.id}`)}
                  className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm ${
                    v.id === result.id ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <span>
                    Version {v.version} · {v.status}
                  </span>
                  <span className="text-xs text-gray-500">
                    {v.receivedAt ? new Date(v.receivedAt).toLocaleString() : ''}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-4 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default LabResultDetail;
